/**
 * Runtime white-label branding (Feature #1 + #8).
 * Fetches the central Branding-Config from the backend and applies it to the
 * document at runtime (title, theme-color, favicon) — no rebuild needed. The
 * Excalidraw MIT attribution (`poweredByExcalidraw`) is surfaced, never stripped.
 */

import { ryfeApi } from "./api";

import type { Branding } from "./api";

export const DEFAULT_BRANDING: Branding = {
  productName: "RYFE — Whiteboard",
  logo: "/branding/LOGO_SIMPEL.png",
  favicon: "/branding/favicon-R.png",
  wikiUrl: "",
  themeColor: "#1a1a1a",
  poweredByExcalidraw: true,
};

/** Fetch branding; falls back to defaults if the backend is unreachable. */
export async function fetchBranding(): Promise<Branding> {
  try {
    return await ryfeApi.getBranding();
  } catch {
    return DEFAULT_BRANDING;
  }
}

/** Apply branding to the current document (idempotent). */
export function applyBranding(branding: Branding): void {
  if (typeof document === "undefined") {
    return;
  }
  document.title = branding.productName;

  const setMeta = (name: string, content: string) => {
    let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  setMeta("theme-color", branding.themeColor);

  if (branding.favicon) {
    // Block E: the R-tile favicon must win in EVERY icon slot — the browser tab
    // (rel=icon, incl. the sized 16/32 variants Excalidraw hard-codes) AND the
    // homescreen (apple-touch-icon), or the old icon lingers where we didn't
    // override it. Point every existing icon link at the branding favicon and
    // ensure a rel=icon + apple-touch-icon exist.
    const iconSelectors = 'link[rel="icon"], link[rel="apple-touch-icon"]';
    const existing = Array.from(
      document.querySelectorAll<HTMLLinkElement>(iconSelectors),
    );
    for (const el of existing) {
      el.setAttribute("href", branding.favicon);
    }
    const ensure = (rel: string) => {
      if (!document.querySelector(`link[rel="${rel}"]`)) {
        const link = document.createElement("link");
        link.setAttribute("rel", rel);
        link.setAttribute("href", branding.favicon);
        document.head.appendChild(link);
      }
    };
    ensure("icon");
    ensure("apple-touch-icon");
  }

  applyTheme(branding.theme);
}

// V1.2 Block 8 — instance theming. Values arrive server-VALIDATED (hex colors,
// whitelisted font-family — never URLs); this only re-declares the CSS custom
// properties from Block 1. The injected sheet loads after both ryfe.scss and
// the editor CSS, so the same names also recolor the editor (.excalidraw).
const THEME_STYLE_ID = "ryfe-theme";
const SLOT_TO_VAR: Record<string, string[]> = {
  primary: ["--color-primary", "--ryfe-ink"],
  "brand-hover": ["--color-brand-hover"],
  "brand-active": ["--color-brand-active"],
  promo: ["--color-promo"],
  "logo-icon": ["--color-logo-icon"],
};
const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;
const FONT_RE = /^[A-Za-z0-9 ,'"-]{1,120}$/;

export function applyTheme(theme: Branding["theme"]): void {
  const prev = document.getElementById(THEME_STYLE_ID);
  if (!theme) {
    prev?.remove();
    return;
  }
  const decls: string[] = [];
  const darkDecls: string[] = [];
  for (const [slot, vars] of Object.entries(SLOT_TO_VAR)) {
    const v = theme.colors?.[slot];
    // defense-in-depth: re-validate client-side before emitting into a sheet
    if (v && HEX_RE.test(v)) {
      for (const name of vars) {
        decls.push(`${name}: ${v};`);
      }
    }
    const d = theme.dark?.[slot];
    if (d && HEX_RE.test(d)) {
      for (const name of vars) {
        darkDecls.push(`${name}: ${d};`);
      }
    }
  }
  if (theme.font && FONT_RE.test(theme.font)) {
    decls.push(`--ryfe-font: ${theme.font};`);
  }
  const css = [
    decls.length ? `:root, .excalidraw { ${decls.join(" ")} }` : "",
    theme.font && FONT_RE.test(theme.font)
      ? `body { font-family: ${theme.font}, system-ui, sans-serif; }`
      : "",
    darkDecls.length
      ? `:root[data-ryfe-theme="dark"], .excalidraw.theme--dark { ${darkDecls.join(
          " ",
        )} }`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
  if (!css) {
    prev?.remove();
    return;
  }
  const el = prev || document.createElement("style");
  el.id = THEME_STYLE_ID;
  el.textContent = css;
  if (!prev) {
    document.head.appendChild(el);
  }
}
